/**
 * Marketing articles.
 *
 * Content is data, not JSX, so adding a post is writing prose rather than
 * writing markup, and every post renders through one component that already
 * handles width, type scale and tables.
 *
 * Two rules hold here as firmly as they do in the product:
 *
 *   * **No invented facts about other companies.** Competitor pricing and
 *     feature sets change constantly, and a comparison table full of numbers
 *     we made up would be wrong within a month and dishonest immediately.
 *     Comparison pieces therefore compare *dimensions* — what to look at and
 *     what the answers mean — and send the reader to the vendor's own page for
 *     anything specific. Where a competitor is named, only stable category
 *     facts are stated.
 *   * **No claim the product cannot back.** Where this product is weaker, the
 *     articles say so by name. An honest weakness list is the only thing that
 *     makes the rest of the page worth believing.
 */

import type { Block } from './blocks';
import generated from './posts.generated.json';

export type { Block };

export type Post = {
  slug: string;
  title: string;
  description: string;
  category: 'Comparisons' | 'Guides' | 'Evidence' | 'Product';
  published: string;
  minutes: number;
  body: Block[];
};

/** Shared closing note for anything touching enforceability. */
const NOT_LEGAL_ADVICE: Block = {
  kind: 'note', tone: 'warn', title: 'Not legal advice',
  text: 'This article describes how the product works and what to look for '
    + 'when comparing tools. It is not legal advice, and nothing here tells '
    + 'you whether a particular agreement is enforceable where you are. For '
    + 'that, ask a lawyer in your jurisdiction.',
};

const WRITTEN: Post[] = [
  {
    slug: 'docusign-alternatives-what-to-compare',
    title: 'Looking for a DocuSign alternative? Compare these seven things',
    description:
      'Most comparison pages are feature checklists that all look the same. '
      + 'Here are the seven questions that actually separate e-signature '
      + 'tools once you are past the first week.',
    category: 'Comparisons',
    published: '2026-10-20',
    minutes: 7,
    body: [
      { kind: 'p', text:
        'Every e-signature product uploads a PDF, puts boxes on it and emails '
        + 'a link. That part is solved, and it is why comparison pages all '
        + 'read the same. The differences show up later — at renewal, at the '
        + 'first delivery failure, and on the day somebody asks you to prove '
        + 'what was signed.' },
      { kind: 'p', text:
        'These are the questions worth asking a vendor, including us. We have '
        + 'answered each one for this product, and said plainly where the '
        + 'answer is "no".' },

      { kind: 'h', text: '1. What happens to your agreements if you stop paying?' },
      { kind: 'p', text:
        'This is the question with the widest range of answers and the least '
        + 'prominent placement. Some tools keep your completed documents '
        + 'available read-only. Some put them behind a reactivation. Some give '
        + 'you an export window and then delete. Find the retention and '
        + 'post-cancellation clauses before you sign up, not after.' },
      { kind: 'p', text:
        'Ours: cancelling stops new sends and nothing else — you keep '
        + 'authenticated access to retrieve and export completed agreements, '
        + 'and the export verifies on its own without us. Our retention terms '
        + 'are in the Terms of Service. Ask every vendor on your list the '
        + 'same question, and read the answer rather than the headline.' },

      { kind: 'h', text: '2. Is the pricing per user, per envelope, or both?' },
      { kind: 'p', text:
        'Per-user pricing punishes you for adding a colleague who sends two '
        + 'contracts a year. Envelope limits punish a busy month. Many plans '
        + 'have both, and the envelope limit is often the one in the '
        + 'footnote. Work out your real monthly volume and check what happens '
        + 'when you exceed it: blocked, auto-upgraded, or billed for overage.' },
      { kind: 'p', text:
        'Ours: no per-user pricing. Either an annual plan with a monthly '
        + 'allowance, or prepaid credits at 50 for $5 with each send costing '
        + '10 — five sends a pack, and the credits do not expire. Reaching a '
        + 'limit blocks new sends only; agreements already out for signature '
        + 'can still be completed.' },

      { kind: 'h', text: '3. What exactly is in the audit trail?' },
      { kind: 'p', text:
        '"Audit trail" is used for everything from a real append-only event '
        + 'log to a PDF page listing four timestamps. Ask what is recorded, '
        + 'whether the record can be edited after the fact, and whether you '
        + 'can verify it without the vendor. A log you can only read inside '
        + 'the vendor’s own dashboard is evidence of nothing in a dispute '
        + 'with that vendor.' },

      { kind: 'h', text: '4. Can anyone check a document without an account?' },
      { kind: 'p', text:
        'The person who most needs to verify a signed agreement — a '
        + 'counterparty, an insurer, an auditor — is usually not your '
        + 'customer. If verification requires a login to your account, it is '
        + 'not really verification. Look for a public check and an evidence '
        + 'export that stands on its own.' },

      { kind: 'h', text: '5. Is anything actually sealed?' },
      { kind: 'p', text:
        'This is the most oversold area in the category. A signature image is '
        + 'a picture. A hash proves bytes are unchanged. A cryptographic PDF '
        + 'seal — a signature made with a certificate from a public authority, '
        + 'bound to an independent timestamp — is a third thing, and it is the '
        + 'only one a PDF reader can check by itself. Marketing pages blur all '
        + 'three. Ask which one you are getting.' },
      { kind: 'note', tone: 'warn', title: 'Ours, plainly',
        text: 'We apply the second kind, not the third. Every artifact we '
          + 'produce reports the PDF seal, certificate trust and independent '
          + 'timestamp as "not applied" rather than as passing, so you can '
          + 'tell by looking rather than by asking. If you need a sealed PDF '
          + 'today, buy from someone who applies one today.' },

      { kind: 'h', text: '6. What does the person signing have to do?' },
      { kind: 'p', text:
        'Your signers judge you, not the vendor. Check whether they need an '
        + 'account, an app or a download; whether the full document is on '
        + 'screen before they commit; and whether it works properly on a '
        + 'phone rather than being a desktop page that technically loads.' },

      { kind: 'h', text: '7. What is missing?' },
      { kind: 'p', text:
        'Ask every vendor what their product does not do. The answer tells you '
        + 'more than the feature list. Here is ours: no bulk send, no SSO, no '
        + 'conditional fields, no approval routing, no custom domains, no '
        + 'mobile app, no in-app template editor yet (templates work through '
        + 'the API), no SMS codes, and no applied seal. If two of those are '
        + 'dealbreakers, a larger product is the right answer and we would '
        + 'rather you found that out now.' },

      { kind: 'h', text: 'A table you can take to any vendor' },
      { kind: 'table',
        head: ['Question', 'Why it bites later'],
        rows: [
          ['Retention after cancellation', 'Found out at the worst moment'],
          ['Per-user or per-envelope', 'The cost model, not the sticker price'],
          ['Audit trail contents', 'Decides whether you can prove anything'],
          ['Independent verification', 'The checker is rarely your customer'],
          ['Seal: image, hash or certificate', 'Three different things, one word'],
          ['Signer friction', 'Your reputation, not the vendor’s'],
          ['Named gaps', 'Vendors that answer are the ones to trust'],
        ] },
      { kind: 'note', tone: 'info', title: 'Check current pricing at source',
        text: 'We deliberately do not publish competitors’ prices or '
          + 'feature tables. They change often, and a stale number on our page '
          + 'would be wrong in our favour. Take the questions above to the '
          + 'vendor’s own pricing page.' },
      NOT_LEGAL_ADVICE,
    ],
  },

  {
    slug: 'where-esignloop-fits',
    title: 'Where this product fits — and who should buy something else',
    description:
      'An honest positioning piece: the three kinds of buyer this is built '
      + 'for, and the four kinds who should pick a bigger product.',
    category: 'Comparisons',
    published: '2026-10-20',
    minutes: 5,
    body: [
      { kind: 'p', text:
        'The established e-signature platforms are large, mature and sell '
        + 'mainly to organizations with procurement departments. We are not '
        + 'competing with that, and pretending otherwise would waste your '
        + 'time and ours.' },

      { kind: 'h', text: 'Built for' },
      { kind: 'ul', items: [
        'People who send a handful of agreements a month and resent paying a '
          + 'per-seat subscription for it. Prepaid credits exist for exactly '
          + 'this: 50 for $5, 10 a send, no expiry and nothing renewing.',
        'Anyone whose agreements get questioned later — contractors, '
          + 'landlords, studios, consultants. The evidence export is the '
          + 'reason this product exists, and it verifies without us.',
        'Developers embedding signing into their own product. The API, '
          + 'webhooks and embedded sessions are real and documented, and the '
          + 'signing page can be hosted inside your application without '
          + 'suppressing consent or evidence controls.',
      ] },

      { kind: 'h', text: 'Buy something else if' },
      { kind: 'ul', items: [
        'You need to send the same document to two hundred people at once. '
          + 'There is no bulk send.',
        'You need SSO, SCIM provisioning or a procurement-grade security '
          + 'questionnaire answered this quarter. We are small.',
        'You need conditional fields, approval chains or routing that '
          + 'branches. Routing here is sequential or parallel, and that is all.',
        'You need a sealed PDF today — a certificate-backed signature a PDF '
          + 'reader validates on its own. We do not apply one.',
      ] },

      { kind: 'h', text: 'What we do differently' },
      { kind: 'p', text:
        'Three things, and they are all consequences of the same decision: to '
        + 'say only what we can show.' },
      { kind: 'ol', items: [
        'The evidence is checkable without us. Exports ship with the public '
          + 'key, the digests and the commands to recompute them. You are not '
          + 'asked to trust a dashboard.',
        'The audit record is append-only and hash-chained, with signed '
          + 'checkpoints, so removing an entry is detectable as well as '
          + 'editing one. Corrections are added, never rewritten.',
        'Assurance is reported precisely. Where no seal is applied, the '
          + 'artifact says "not applied" — not "passed", and not "invalid".',
      ] },
      { kind: 'quote', text:
        'A hash proves the bytes are unchanged. It does not prove who signed, '
        + 'what they intended, or that an agreement is enforceable anywhere in '
        + 'particular. Any product that implies otherwise is selling you a '
        + 'feeling.' },
      NOT_LEGAL_ADVICE,
    ],
  },

  {
    slug: 'free-electronic-signature-what-it-costs',
    title: 'What "free e-signature" usually costs you',
    description:
      'Free tiers are real and often the right answer. Here is what to check '
      + 'before you put a client agreement through one.',
    category: 'Guides',
    published: '2026-10-20',
    minutes: 4,
    body: [
      { kind: 'p', text:
        'Plenty of tools will let you sign a PDF for nothing, and for a lot of '
        + 'documents that is genuinely the right choice. The question is what '
        + 'the free tier is funded by, because something always is.' },
      { kind: 'h', text: 'The four common trade-offs' },
      { kind: 'ul', items: [
        'Volume that resets. Three documents a month is fine until the month '
          + 'you have five, which is usually the month that matters.',
        'No retention guarantee. Free tiers rarely commit to keeping your '
          + 'completed documents, and "download it yourself" is a policy, not '
          + 'a feature.',
        'A thinner record. The audit trail on a free tier is often a summary '
          + 'page rather than a verifiable log.',
        'Your data as the product. Check what the privacy policy permits, '
          + 'particularly around training and analytics on document content.',
      ] },
      { kind: 'h', text: 'When free is the right answer' },
      { kind: 'p', text:
        'Signing something yourself, countersigning a document you have '
        + 'already negotiated, or anything where nobody is going to dispute '
        + 'what was agreed. For those, use the free tool and do not think '
        + 'about it again.' },
      { kind: 'h', text: 'When it is not' },
      { kind: 'p', text:
        'Anything where the amount, the scope or the dates could be argued '
        + 'about later. The cost of being unable to show what the other party '
        + 'actually saw is not measured in subscription dollars.' },
      { kind: 'note', tone: 'info', title: 'What we do for free',
        text: 'Preparing an agreement costs nothing and needs no account — '
          + 'upload, place fields, and see the whole signing experience before '
          + 'deciding. Self-signing, where you are the only signer, is '
          + 'unlimited on every paid tier. Verification is free and public, '
          + 'forever, for anyone.' },
    ],
  },

  {
    slug: 'are-electronic-signatures-legally-binding',
    title: 'Are electronic signatures legally binding?',
    description:
      'The honest version: usually yes for ordinary agreements, with real '
      + 'exceptions — and what actually matters is the evidence behind the '
      + 'signature.',
    category: 'Guides',
    published: '2026-10-20',
    minutes: 5,
    body: [
      { kind: 'p', text:
        'This is the most-searched question in the category and the one most '
        + 'often answered badly. The usual version — "yes, electronic '
        + 'signatures are legally binding!" with a logo wall underneath — is '
        + 'marketing, not an answer.' },
      { kind: 'h', text: 'The general position' },
      { kind: 'p', text:
        'Many jurisdictions have legislation recognising electronic '
        + 'signatures for most everyday agreements, and the broad direction '
        + 'of travel has been toward recognition rather than away from it. In '
        + 'practice, for ordinary commercial agreements between willing '
        + 'parties, an electronic signature is usually treated the same as a '
        + 'handwritten one.' },
      { kind: 'h', text: 'The exceptions are real' },
      { kind: 'p', text:
        'Specific categories are commonly excluded or carry extra '
        + 'formalities. Wills and testamentary instruments, some property and '
        + 'land instruments, certain family-law and court documents, and some '
        + 'notarised instruments are the ones that come up most. The list '
        + 'differs by jurisdiction, and "it worked for my contracts" tells you '
        + 'nothing about whether it works for a deed.' },
      { kind: 'h', text: 'What actually decides a dispute' },
      { kind: 'p', text:
        'Rarely the question of whether electronic signatures are valid in '
        + 'principle. Far more often: can you show what the other person '
        + 'saw, that they meant to sign it, and that the document has not '
        + 'changed since? That is an evidence question, and it is the one a '
        + 'product can actually help with.' },
      { kind: 'ul', items: [
        'The exact bytes of the document each person was shown, fixed at the '
          + 'moment of sending.',
        'Who acted, when, how they authenticated, and whether that '
          + 'authentication passed.',
        'Consent to sign electronically, recorded with the version of the '
          + 'wording they were shown.',
        'A record that cannot be quietly edited afterwards — including by the '
          + 'vendor.',
      ] },
      { kind: 'h', text: 'What a hash does and does not do' },
      { kind: 'p', text:
        'A SHA-256 digest proves a file has not changed since the digest was '
        + 'recorded. That is genuinely useful and it is not identity. It does '
        + 'not establish who signed, what they intended, or that an agreement '
        + 'is enforceable. Keeping those claims separate is the difference '
        + 'between evidence and decoration.' },
      NOT_LEGAL_ADVICE,
    ],
  },

  {
    slug: 'what-makes-an-audit-trail-worth-anything',
    title: 'What makes an audit trail worth anything',
    description:
      'Most audit trails are a list. The useful property is that nobody — '
      + 'including the vendor — can quietly change it.',
    category: 'Evidence',
    published: '2026-10-20',
    minutes: 6,
    body: [
      { kind: 'p', text:
        'Every e-signature tool produces an audit trail. Almost all of them '
        + 'are a table of events the vendor wrote and the vendor stores. In a '
        + 'dispute with a third party that may be plenty. In a dispute where '
        + 'the record itself is questioned, a list the vendor could have '
        + 'rewritten is not much of an answer.' },
      { kind: 'h', text: 'Three properties to look for' },
      { kind: 'ol', items: [
        'Append-only. Corrections are added as new entries rather than '
          + 'editing old ones, and the storage layer enforces it rather than '
          + 'the application promising it.',
        'Chained. Each entry commits to the one before it, so changing an '
          + 'entry invalidates everything after it.',
        'Anchored. Signed checkpoints commit to the whole set, so *deleting* '
          + 'an entry is detectable too — which a chain alone does not give '
          + 'you, because a truncated chain is still a valid chain.',
      ] },
      { kind: 'h', text: 'Why the third one matters' },
      { kind: 'p', text:
        'This is the part most implementations miss. If each record points '
        + 'back at the previous one, altering record 7 breaks records 8 '
        + 'onward and is obvious. But simply removing records 8 onward leaves '
        + 'a shorter chain that still verifies perfectly. Only a commitment to '
        + 'the complete set — a signed checkpoint over an ordered tree of all '
        + 'the entries — catches that.' },
      { kind: 'h', text: 'And then: can you check it yourself?' },
      { kind: 'p', text:
        'Verification that only happens inside the vendor’s dashboard is '
        + 'verification by assertion. The useful version ships you the public '
        + 'key, the digests and the method, so you or your lawyer can '
        + 'recompute everything offline, years later, with no account.' },
      { kind: 'note', tone: 'ok', title: 'How this product does it',
        text: 'Events are append-only — the database refuses updates and '
          + 'deletes outright — hash-chained, and anchored in signed '
          + 'checkpoints over an ordered tree of the whole set. The evidence '
          + 'export includes the public key, every digest and the commands to '
          + 'recompute them. There is a public verifier that needs no account.' },
    ],
  },

  {
    slug: 'pay-as-you-go-vs-subscription',
    title: 'Prepaid credits or an annual plan: which is cheaper for you',
    description:
      'Arithmetic rather than persuasion. The break-even is lower than most '
      + 'people guess.',
    category: 'Product',
    published: '2026-10-20',
    minutes: 4,
    body: [
      { kind: 'p', text:
        'We sell both, so we have no stake in which you pick. Here is the '
        + 'arithmetic.' },
      { kind: 'h', text: 'The two shapes' },
      { kind: 'p', text:
        'Credits are prepaid: 50 credits for $5, each send costs 10, so a '
        + 'pack is five sends and works out at a dollar a send. Nothing '
        + 'renews, and credits never expire. An annual plan is a subscription '
        + 'with a monthly allowance that resets whether you used it or not.' },
      { kind: 'table',
        head: ['Sends per month', 'Credits', 'Standard — 5 a month',
               'Pro — 25 a month'],
        rows: [
          ['1', '$12 a year', '$29.99 a year', '$120 a year'],
          ['2', '$24 a year', '$29.99 a year', '$120 a year'],
          ['3', '$36 a year', '$29.99 a year', '$120 a year'],
          ['5', '$60 a year', '$29.99 a year — at the cap', '$120 a year'],
          ['10', '$120 a year', 'Over the cap', '$120 a year'],
          ['25', '$300 a year', 'Over the cap', '$120 a year — at the cap'],
        ] },
      { kind: 'p', text:
        'The break-even against credits sits between two and three sends a '
        + 'month. Below it, credits win and you are never billed for a quiet '
        + 'month. Above it the plan wins quickly — at five a month Standard '
        + 'is half the price of credits, and at twenty-five a month Pro is '
        + 'two-fifths.' },
      { kind: 'p', text:
        'The allowance is a ceiling, not a target. Standard includes five '
        + 'sends a subscription month and Pro twenty-five; reaching the limit '
        + 'blocks new sends until the next reset rather than charging an '
        + 'overage, and your drafts, existing signing links and archive stay '
        + 'exactly as they were.' },
      { kind: 'h', text: 'Reasons that are not about price' },
      { kind: 'ul', items: [
        'API access, webhooks and embedded signing are Pro-plan features. '
          + 'Credits do not include them.',
        'Credits never expire, so buying one pack for an occasional need is '
          + 'not a use-it-or-lose-it decision.',
        'Automatic top-up exists if you want credits without thinking about '
          + 'them: when the balance can no longer cover a send, one more $5 '
          + 'pack is bought. It is off by default and can be turned off at any '
          + 'time.',
      ] },
      { kind: 'note', tone: 'info', title: 'Either way',
        text: 'One sent agreement is one charge, however many signers, files '
          + 'or fields it has. Reminders are free. Self-signing is unlimited. '
          + 'A send that could not be delivered at all is refunded; one that '
          + 'went out and was later voided or declined stays charged, because '
          + 'the send happened.' },
    ],
  },

  {
    slug: 'signature-image-hash-or-seal',
    title: 'Signature image, hash, or seal: three things with one name',
    description:
      'The category uses "secure" for all three. They are very different, and '
      + 'knowing which you have is the whole point.',
    category: 'Evidence',
    published: '2026-10-20',
    minutes: 5,
    body: [
      { kind: 'p', text:
        'Ask a vendor whether documents are "securely signed" and you will get '
        + 'a yes. Ask which of these three they mean and the conversation gets '
        + 'much more useful.' },
      { kind: 'h', text: '1. A signature image' },
      { kind: 'p', text:
        'The drawn or typed squiggle placed on the page. It is a picture. It '
        + 'carries no cryptographic meaning at all — it can be copied out of '
        + 'one PDF and pasted into another in about a minute. Its value is '
        + 'that it looks like what people expect a signature to look like, and '
        + 'it is evidence of intent in context, not of integrity.' },
      { kind: 'h', text: '2. A hash, or integrity record' },
      { kind: 'p', text:
        'A SHA-256 digest of the document’s exact bytes, recorded at a '
        + 'known moment. Later, anyone can recompute it and see whether the '
        + 'file has changed. This is real and verifiable — and it proves '
        + 'exactly one thing. It does not say who signed, or when, unless '
        + 'those facts are recorded alongside it in a record that is itself '
        + 'protected.' },
      { kind: 'h', text: '3. A cryptographic seal' },
      { kind: 'p', text:
        'A digital signature applied to the PDF itself, made with a private '
        + 'key whose certificate was issued to a named organization by a '
        + 'public certificate authority, and bound to a timestamp from an '
        + 'independent authority. The difference that matters: a PDF reader '
        + 'validates this on its own, against a chain of trust that does not '
        + 'come from the vendor. The reader shows who finalized the document '
        + 'and when, and whether anything has changed since.' },
      { kind: 'h', text: 'Which should you care about?' },
      { kind: 'p', text:
        'For most everyday agreements, the image plus a well-protected '
        + 'evidence record is proportionate. A seal starts to matter when the '
        + 'document will be handled by people who have no relationship with '
        + 'you or your vendor, and who need their own software to tell them '
        + 'the file is intact.' },
      { kind: 'note', tone: 'warn', title: 'What this product applies today',
        text: 'A signature image, and an integrity record with a protected '
          + 'audit trail. No cryptographic PDF seal, no externally trusted '
          + 'certificate and no independent timestamp. Every artifact we '
          + 'produce says so in those words rather than leaving you to infer '
          + 'it.' },
    ],
  },

  {
    slug: 'sign-a-pdf-on-your-phone',
    title: 'How to get a PDF signed from your phone, start to finish',
    description:
      'A practical walkthrough: upload, place fields, send, and what the '
      + 'other person sees — all from a handset.',
    category: 'Guides',
    published: '2026-10-20',
    minutes: 4,
    body: [
      { kind: 'p', text:
        'Most e-signature products were built for a desktop and later made to '
        + 'load on a phone. The difference shows up at the fiddly part: '
        + 'placing fields on a page you can barely see. Here is the whole '
        + 'round trip on a handset.' },
      { kind: 'h', text: 'Sending' },
      { kind: 'ol', items: [
        'Open the prepare page and pick a PDF — from Files, iCloud, Drive or '
          + 'a photo scan. No account yet.',
        'Tap where a field goes. Tap again with a different field type '
          + 'selected for dates, text or checkboxes. Fields can be dragged and '
          + 'removed.',
        'Add recipients and choose whether they sign one after another or all '
          + 'at once.',
        'Choose how each person proves who they are: the emailed link alone, '
          + 'or a six-digit code sent to them first.',
        'Send. The document is frozen at that moment, and that exact version '
          + 'is what every recipient sees.',
      ] },
      { kind: 'h', text: 'What the other person does' },
      { kind: 'p', text:
        'They tap the link in their email. The agreement opens — no account, '
        + 'no app, no download. They read the terms on screen, agree to sign '
        + 'electronically, enter a code if you asked for one, fill in their '
        + 'fields and draw or type a signature with a finger. Then they get '
        + 'their own copy.' },
      { kind: 'h', text: 'What you get back' },
      { kind: 'p', text:
        'The completed PDF with the signatures in place, a certificate of '
        + 'completion, and an evidence record covering every step — who '
        + 'opened it, when, how they authenticated and what they consented '
        + 'to. Export it whenever you like — the export verifies on its own, '
        + 'without this service.' },
      { kind: 'note', tone: 'ok', title: 'Try it without signing up',
        text: 'Preparing costs nothing and needs no account. A card is '
          + 'required only when you actually send, and work you prepared '
          + 'beforehand carries over into your account.' },
    ],
  },
];

/**
 * Everything published, newest first.
 *
 * `WRITTEN` above is hand-authored in TypeScript, where the type checker is
 * worth having. `posts.generated.json` is appended to by
 * `scripts/generate-post.mjs` on a schedule — data rather than code, because
 * a robot editing a TypeScript literal is a robot that can break the build
 * in ways review will not catch.
 *
 * Generated posts come first so the newest is the featured one. The cast is
 * the one place the two halves meet: the generator validates every block
 * against the same `Block` vocabulary before it writes, so if that validation
 * is ever loosened, this is the line that stops being true.
 */
export const POSTS: Post[] = [...(generated as Post[]), ...WRITTEN];

export const CATEGORIES = ['Comparisons', 'Guides', 'Evidence', 'Product'] as const;

export function postBySlug(slug: string | undefined): Post | undefined {
  return POSTS.find((post) => post.slug === slug);
}

/** Other posts worth reading next: same category first, then the rest. */
export function related(post: Post, limit = 3): Post[] {
  const others = POSTS.filter((item) => item.slug !== post.slug);
  const sameCategory = others.filter((item) => item.category === post.category);
  const rest = others.filter((item) => item.category !== post.category);
  return [...sameCategory, ...rest].slice(0, limit);
}
